// Bitácora de cambios que se muestra en el ambiente de desarrollo.
//
// Cómo agregarle cambios:
//   1. Antes de un deploy a desarrollo, agrega un objeto NUEVO al inicio del arreglo
//      (el primer elemento siempre se considera la versión actual).
//   2. Si la versión ya existe, sólo agrega entradas a su arreglo `cambios`.
//   3. Actualiza `versionDev` en `src/AppVersion.js` para que coincida con la versión
//      más reciente de esta lista.
//
// Tipos disponibles: "nuevo" | "mejora" | "correccion" | "eliminado"
// `modulo` es opcional y sirve para agrupar visualmente (Constructor, Herramientas, etc.).

const CHANGELOG = [
  {
    version: "2.0.29-beta",
    fecha: "2026-09-30",
    cambios: [
      {
        tipo: "nuevo",
        modulo: "NIA",
        descripcion:
          "NIA puede armar presentaciones. En \"Acciones predeterminadas NIA\" hay un tipo de salida nuevo, \"Presentación\", y la acción que lo usa responde dentro del chat con un visor de láminas: una portada y hasta 10 diapositivas de contenido, con flechas para avanzar (también con el teclado), las notas del orador a la vista con el botón \"Notas\" y pantalla completa para proyectar desde el propio chat. Con \"Descargar .pptx\" se baja el mismo contenido como archivo de PowerPoint, lámina por lámina igual a lo que se ve en el visor. El tipo que antes se llamaba \"Actividad / Evaluación\" ahora dice \"Contenido estructurado\".",
      },
      {
        tipo: "nuevo",
        modulo: "NIA",
        descripcion:
          "En \"Acciones predeterminadas NIA\" cada acción indica dónde se muestra: en la lección, en el libro o en los dos. Cada lugar tiene su propio prompt y la acción sólo aparece donde está marcada y tiene prompt escrito; el formulario avisa cuando un prompt está vacío o cuando la acción no quedaría visible en ningún lado. Si un rol no tiene ninguna acción para el lugar donde está, NIA no aparece ahí: con acciones sólo de lección, por ejemplo, NIA no se muestra en la portada del libro. Las acciones que ya existían se muestran en los dos, como hasta ahora.",
      },
      {
        tipo: "nuevo",
        modulo: "NIA",
        descripcion:
          "Las acciones de NIA se ordenan por rol. El gestor las agrupa por rol y se arrastran las tarjetas para decidir en qué orden las ve ese rol dentro de NIA; cada rol tiene su propio orden. Para reordenar hay que quitar los filtros y la búsqueda, porque con la lista incompleta el orden quedaría a medias. Cada tarjeta muestra además si la acción sale en la lección, en el libro o en ambos.",
      },
      {
        tipo: "nuevo",
        modulo: "Herramientas",
        descripcion:
          "En Descarga de PDF y DOCX se agregó el \"Bloc de Actividades\", un DOCX pensado para el cuaderno de trabajo del libro, en tres versiones: Alumno (sin respuestas ni bloques exclusivos del profesor), Profesor (con respuestas y esos bloques) y Alumno + Profesor, que entrega los dos en un ZIP con las actividades aleatorias en el mismo orden. Al elegirlo aparecen sus ajustes a la derecha: mostrar la imagen y las opciones de las preguntas en tabla, usar formato de respuesta corta (el profesor ve sólo la respuesta correcta y el alumno recibe un renglón \"R:\"), no exportar los bloques de Título, Texto o HTML, y exportar la Autoevaluación en versión ligera, con una sola columna de círculos. Se puede generar por libro, en lote y desde la generación masiva de DOCX por lección.",
      },
      {
        tipo: "nuevo",
        modulo: "Reportes",
        descripcion:
          "\"Exportar reporte completo\" de los reportes de licencias por plantel y por corporativo pregunta qué reportes va a llevar el archivo: Por alumno, Por libro y Por ecosistemas, uno o varios. Arranca con Por alumno marcado, que es lo que se descargaba antes, así que quien no cambie nada recibe el mismo archivo de siempre. Cada plantel lleva una hoja por reporte elegido y al final vienen las hojas de resumen de cada uno. Como la descarga recorre todos los planteles, pedir varios reportes a la vez ahorra repetir esa espera.",
      },
      {
        tipo: "mejora",
        modulo: "Herramientas",
        descripcion:
          "Los textos de ayuda de las tarjetas de Herramientas Nimbus GED se reescribieron para decir qué hace cada herramienta, en lugar del genérico \"Con esta herramienta podrás…\". En \"Gestión de contraseñas\" el texto cambia según quien entra: el padre o tutor lee que ahí consulta la contraseña de sus hijos y el profesor, la de sus alumnos.",
      },
      {
        tipo: "mejora",
        modulo: "Herramientas",
        descripcion:
          "Las tarjetas \"Generar examen con IA\" y \"Generar tarea con IA\" dicen que parten de una o más lecciones de los libros del profesor, porque se pueden elegir hasta 8 lecciones del mismo libro. Así un examen o una tarea pueden cubrir una unidad completa.",
      },
      {
        tipo: "mejora",
        modulo: "NIA",
        descripcion:
          "El campo para escribirle a NIA crece conforme se escribe, hasta tres renglones, y a partir de ahí hace scroll. Enter envía el mensaje y Shift + Enter agrega un salto de línea.",
      },
      {
        tipo: "mejora",
        modulo: "Plataforma",
        descripcion:
          "En Mi perfil, el alumno ya no está obligado a capturar su fecha de nacimiento ni su teléfono: los dos dicen \"(opcional)\" y se puede guardar sin ellos. Además, a las cuentas que se dieron de alta sin fecha de nacimiento ya no se les muestra el 31 de diciembre de 1969 o el 1 de enero de 1970 como si la hubieran capturado; el campo aparece vacío.",
      },
      {
        tipo: "mejora",
        modulo: "Plataforma",
        descripcion:
          "\"Cambiar rol\" sólo aparece en el menú del usuario cuando éste tiene más de un rol, como ya pasaba con \"Cambiar plantel\". Antes salía siempre, aunque no hubiera otro rol que elegir.",
      },
      {
        tipo: "correccion",
        modulo: "Reportes",
        descripcion:
          "\"Exportar reporte completo\" ya no falla con planteles cuyos nombres empiezan igual. Excel sólo admite 31 caracteres en el nombre de una hoja; al recortarlos, dos planteles como \"… Campus Norte\" y \"… Campus Sur\" quedaban con el mismo nombre y la descarga completa se perdía. Ahora se conservan el principio y el final del nombre, y si aun así se repite se numera.",
      },
      {
        tipo: "correccion",
        modulo: "Reportes",
        descripcion:
          "En la pantalla de libros del reporte del profesor, las evaluaciones salen en el orden en que aparecen en el libro. Antes salían en el orden en que se agregaron las lecciones, así que una evaluación que se había subido o bajado en el constructor aparecía fuera de lugar.",
      },
      {
        tipo: "correccion",
        modulo: "Constructor",
        descripcion:
          "Los bloques de texto con imagen de fondo ya no fuerzan el texto a blanco. Al elegir un fondo, el texto se pintaba de blanco sin importar el color que se le hubiera puesto, y sobre fondos claros no se leía. Ahora conserva su color. En el Editor de fondos de texto, el interruptor de color de la vista previa sólo sirve para ver cómo se lee el fondo y ya no se guarda con él.",
      },
      {
        tipo: "correccion",
        modulo: "Visor",
        descripcion:
          "En la actividad de carrusel el texto de la diapositiva ya no se ve cortado en las orillas.",
      },
    ],
  },
  {
    version: "2.0.28-beta",
    fecha: "2026-09-17",
    cambios: [
      {
        tipo: "nuevo",
        modulo: "Herramientas",
        descripcion:
          "Generar examen con IA y Generar tarea con IA tienen un campo nuevo, \"Indicaciones adicionales\": ahí el profesor escribe con sus palabras lo que quiere —en qué parte de la lección enfocarse, qué evitar, qué tipo de reactivos prefiere, cuántas opciones llevan las preguntas— y la IA lo obedece. Son hasta 500 caracteres y es opcional: si se deja vacío, el examen o la tarea se generan exactamente igual que antes.",
      },
      {
        tipo: "nuevo",
        modulo: "Herramientas",
        descripcion:
          "En Generar examen con IA se eligen los tipos de reactivo que va a llevar: Preguntas, Relacionar, Ordenar y Arrastrar. Arranca con Preguntas marcada, que es el caso más común, y se marcan los demás si se quieren. Lo elegido no es una sugerencia: si la IA propone un bloque de un tipo que no se pidió, se descarta al guardar, así que el examen sale con lo que el profesor pidió y no con lo que a ella le pareció.",
      },
      {
        tipo: "mejora",
        modulo: "Herramientas",
        descripcion:
          "El examen generado con IA abre con otro encabezado: partido a la mitad, con el título en blanco sobre una ilustración del lado izquierdo y una imagen de salón de clases del derecho, en lugar del recuadro con la imagen de fondo. Se puede cambiar desde el constructor como cualquier otro título.",
      },
      {
        tipo: "mejora",
        modulo: "Herramientas",
        descripcion:
          "Los listados de exámenes y de tareas se ordenan del más reciente al más viejo. Antes salían en el orden en que se crearon, así que lo que el profesor acababa de hacer quedaba hasta el final de la lista y había que bajar a buscarlo. También cambia el orden con el que abren las listas del alumno, que de todos modos conservan su selector para ordenarlas al revés.",
      },
      {
        tipo: "mejora",
        modulo: "Constructor",
        descripcion:
          "La ventana \"Mover a carpeta\" quedó con el mismo estilo que el resto de la plataforma: encabezado con su título y botón de cerrar, fondo oscurecido y la lista de carpetas dentro de un solo marco, con la carpeta elegida resaltada, en vez de la tabla de recuadros sueltos. Se ve igual al mover un solo contenido o varios a la vez.",
      },
      {
        tipo: "correccion",
        modulo: "Constructor",
        descripcion:
          "Al abrir la Vista previa de una lección ya no se alcanza a ver la plataforma vacía —el encabezado y el menú lateral sin nada dentro— antes de que cargue el contenido. Ahora aparece un indicador de carga y enseguida la lección.",
      },
      {
        tipo: "correccion",
        modulo: "Visor",
        descripcion:
          "Varias cosas que seguían en español dentro de lecciones y exámenes en inglés ya se traducen. El nombre de la actividad en su portada decía \"PREGUNTAS\" aunque el botón de al lado dijera \"Start!\"; el botón \"Enviar y cerrar\" de arrastrar, relacionar y llenar huecos nunca cambiaba de idioma; y lo mismo pasaba con \"Revelar carta\", \"Enviar todos\", \"Ayuda\" y el botón para escribir la respuesta en las preguntas abiertas. Además, la portada de un examen en inglés mostraba un texto interno del sistema en lugar del título de las instrucciones.",
      },
    ],
  },
  {
    version: "2.0.27-beta",
    fecha: "2026-09-17",
    cambios: [
      {
        tipo: "nuevo",
        modulo: "Herramientas",
        descripcion:
          "En el Creador de exámenes hay una tarjeta nueva, \"Generar examen con IA\": el profesor elige uno de sus libros y una de sus lecciones, le pone título y la IA arma el examen con el contenido de esa lección. En el mismo paso se configura lo que hoy se captura a mano —tiempo límite, número de intentos, si se muestra la calificación y la longitud del código de acceso— y se puede asignar a sus grupos. La cantidad de reactivos se puede fijar o dejar en \"Según el tiempo límite\", que calcula uno por cada cinco minutos, entre 5 y 20. Un reactivo es una pregunta, un par a relacionar o un elemento a ordenar, no un bloque completo: la IA decide en cuántos bloques los reparte y usa sólo los cuatro tipos que un examen admite —Preguntas, Relacionar, Ordenar y Arrastrar—. Los códigos de desbloqueo se generan solos, uno por intento. Lo que sale es un borrador: al terminar se abre en el constructor para revisarlo antes de que lo vean los alumnos.",
      },
      {
        tipo: "nuevo",
        modulo: "Herramientas",
        descripcion:
          "El Creador de tareas tiene la misma tarjeta, \"Generar tarea con IA\": se elige libro y lección, se pone título y la IA redacta la descripción y los criterios de la rúbrica. También se puede asignar a grupos en ese momento. Igual que el examen, se guarda como borrador y se abre en el creador de tareas para revisarlo. Las tareas siguen siendo tareas: no se convierten en lecciones con bloques.",
      },
      {
        tipo: "nuevo",
        modulo: "Herramientas",
        descripcion:
          "Las tareas pueden tener fecha límite de entrega. En el Creador de tareas es un interruptor: apagado, la tarea no vence y el alumno entrega cuando quiera; encendido, se elige el día y a partir del final de ese día el alumno ya no puede enviar ni modificar su entrega. La fecha aparece en el gestor del profesor y en la lista del alumno, en rojo cuando ya pasó.",
      },
      {
        tipo: "nuevo",
        modulo: "Herramientas",
        descripcion:
          "El profesor puede adjuntar material de apoyo a una tarea: hasta diez archivos de 10 MB cada uno, en PDF, Word, Excel, PowerPoint o imagen. El alumno los ve en su tarea con el icono de su formato y los abre desde ahí. El video no se sube como archivo: se pega como liga en la descripción, que ya lo incrusta.",
      },
      {
        tipo: "mejora",
        modulo: "Herramientas",
        descripcion:
          "Los gestores de exámenes y de tareas enseñan de un vistazo cómo va el grupo. En la lista de un grupo, cada examen o tarea trae sus actividades, el tiempo límite o la fecha de entrega, una barra con cuántos alumnos ya terminaron de cuántos —y cuántos lo tienen abierto en este momento— y el promedio del grupo. Al entrar a uno, arriba de la lista hay tarjetas de resumen y los alumnos se pueden ordenar por nombre, calificación o estatus; cada quien trae su estado escrito —Sin presentar, En curso o Terminado— en vez de tener que deducirlo de las columnas vacías. Los exámenes sin actividades se marcan, porque el alumno los abriría en blanco. Los buscadores dejaron de distinguir acentos y mayúsculas, así que \"matematicas\" encuentra \"Matemáticas\", y cuando algo no se puede cargar o la búsqueda no encuentra nada se dice, en lugar de dejar la tabla vacía.",
      },
      {
        tipo: "mejora",
        modulo: "Herramientas",
        descripcion:
          "La calificación de una tarea se expresa sobre 10, como en el resto de la plataforma; antes se mostraba sobre 100. La pantalla de calificar se reacomodó alrededor de eso: los criterios de la rúbrica con su puntaje, la calificación recalculándose conforme se palomean, y los comentarios en el lugar que ocupaba el recuadro de resultados. En el Excel la calificación va como número y no como el texto \"8 / 100\", así que la columna se puede ordenar y promediar.",
      },
      {
        tipo: "correccion",
        modulo: "Visor",
        descripcion:
          "El promedio de tareas del alumno ya no cuenta como cero lo que todavía no tiene calificación. Antes dividía entre todas las tareas asignadas, así que al inicio del curso —una entregada y cuatro por hacer— el promedio salía por los suelos. Ahora sólo cuentan las que ya tienen desenlace: las calificadas con su nota, las rechazadas como cero, y las no entregadas cuyo plazo ya venció como cero. Las que están esperando revisión del profesor no cuentan, aunque la fecha límite haya pasado —el alumno ya entregó—, y las que aún tienen plazo o no tienen fecha límite, tampoco.",
      },
      {
        tipo: "correccion",
        modulo: "Visor",
        descripcion:
          "La pantalla de inicio de un examen ya no se queda en blanco cuando el examen no tiene descripción. El título se tomaba siempre de ese campo, así que un examen sin él tumbaba la pantalla completa y el alumno no podía entrar; ahora, si falta, se usa el identificador del examen.",
      },
      {
        tipo: "nuevo",
        modulo: "Herramientas",
        descripcion:
          "En el Gestor de exámenes, la lista de exámenes de un grupo tiene una acción nueva —el candado— que abre los códigos de desbloqueo de ese examen: uno por cada intento permitido, con su botón para copiarlo y el dato de cuántos alumnos ya lo usaron. Antes había que entrar a la configuración del examen para consultarlos. Si el examen permite más intentos que códigos tiene configurados, se avisa; si todavía no tiene ninguno, se ofrece ir a la configuración a generarlos.",
      },
      {
        tipo: "nuevo",
        modulo: "Reportes",
        descripcion:
          "El reporte de actividades por alumno tiene, arriba de la tabla, un \"Ordenar por\" con dos opciones: Nombre y Apellidos. Al elegir Apellidos la lista se ordena por el primero y el segundo apellido, y el nombre pasa al final —\"Aguilar Santos Eduardo\"—, que es como se leen las listas de asistencia; con Nombre se ve como siempre, \"Eduardo Aguilar Santos\". Antes el reporte sólo se ordenaba por nombre de pila, así que buscar a un alumno del que se sabe el apellido obligaba a recorrer el grupo entero. Los alumnos que comparten apellido quedan siempre en la misma posición entre una carga y otra. El orden elegido y la forma de escribir el nombre se aplican igual a la tabla en pantalla y a los dos archivos de Excel, \"Exportar\" y \"Exportar completo\".",
      },
      {
        tipo: "mejora",
        modulo: "Visor",
        descripcion:
          "Las tablas de Exámenes y Tareas del Visor de recursos quedaron como las del Gestor: mismo estilo de filas, el buscador arriba a la altura de \"Visor de recursos\" —donde ya estaba en la vista de Libros, no debajo de las pestañas— y las fechas escritas en largo, \"10 de septiembre de 2026\" en vez de \"2026-09-10\". El buscador dejó de distinguir acentos y mayúsculas, así que \"matematicas\" encuentra \"Matemáticas\", y busca sobre lo mismo que se ve en la tabla; cuando ningún registro coincide con lo que se escribió lo dice, en lugar de dejar la tabla vacía con los encabezados. En Tareas se agregaron las columnas Fecha de creación y Fecha límite: el profesor no las veía, su tabla sólo traía el nombre de la tarea y las acciones.",
      },
    ],
  },
  {
    version: "2.0.26-beta",
    fecha: "2026-09-17",
    cambios: [
      {
        tipo: "nuevo",
        modulo: "Reportes",
        descripcion:
          "El reporte de licencias de un plantel tiene una tercera vista, \"Vista por ecosistemas\", junto a las de libros y alumnos, que no cambiaron. Muestra un renglón por grupo con los libros que tiene asignados, los ecosistemas a los que pertenecen esos libros, el precio de cada uno, el costo por alumno y el total del grupo. Se puede exportar a Excel con las mismas columnas. Cuando un libro está en varios ecosistemas no se cobran todos: se cobra la menor cantidad de ecosistemas que alcance a cubrir los libros del grupo, así que si uno solo ya los abarca, los demás no se suman aunque compartan algún título. Los libros que no pertenecen a ningún ecosistema se listan aparte y se cobran uno por uno, con las mismas reglas que la vista por alumno: los propios del plantel no se cobran y los híbridos van al precio de la editorial.",
      },
      {
        tipo: "nuevo",
        modulo: "Reportes",
        descripcion:
          "En la \"Vista por ecosistemas\", al hacer clic en la cantidad de alumnos de un grupo se abre la lista de quiénes son, con su correo, nivel y grado. Trae \"Ordenar por: Nombre | Apellidos\" y arranca por apellidos, igual que las listas del plantel, con el nombre separado en Nombre(s), Primer apellido y Segundo apellido.",
      },
      {
        tipo: "nuevo",
        modulo: "General",
        descripcion:
          "Los ecosistemas tienen precio en pesos y en dólares. Se captura en \"Lista de precios\", en la pestaña \"Productos\", en una sección nueva que lista cada ecosistema con sus precios y cuántos libros trae. Es el precio que usa la \"Vista por ecosistemas\" del reporte de licencias; mientras no se capture, ese reporte muestra los ecosistemas en cero. En Herramientas Nimbus GED los ecosistemas se siguen creando y editando igual, con su nombre y sus libros.",
      },
      {
        tipo: "mejora",
        modulo: "Reportes",
        descripcion:
          "En el reporte de licencias de un plantel, las columnas de libros muestran los títulos como etiquetas, con el mismo estilo que los grupos en la lista de alumnos. Se ven hasta cinco y, si hay más, aparece \"Mostrar más\". Antes salían todos seguidos en un solo texto, que en grupos con muchos libros hacía el renglón larguísimo. El Excel sigue saliendo con la lista completa.",
      },
      {
        tipo: "mejora",
        modulo: "General",
        descripcion:
          "Las ventanas de \"Lista de precios\" —editar el precio de un libro, de una licencia, de un producto, de los libros híbridos, eliminar un producto e importar el Excel— quedaron con el estilo del resto de la plataforma: encabezado con título e icono, botón de cerrar, y los botones abajo, separados. La ventana de eliminar un producto avisa que la acción no se puede deshacer.",
      },
      {
        tipo: "correccion",
        modulo: "General",
        descripcion:
          "\"Lista de precios\" abre desde el menú lateral. La opción llevaba a \"Usuarios de la editorial\", así que a la lista de precios sólo se llegaba entrando desde el inicio.",
      },
    ],
  },
  {
    version: "2.0.25-beta",
    fecha: "2026-09-15",
    cambios: [
      {
        tipo: "mejora",
        modulo: "Escolar",
        descripcion:
          "Al usar \"Promover todos\", a los alumnos que se gradúan se les agrega \"_graduado\" al final de su correo (por ejemplo, ana@colegio.edu.mx_graduado), para que el plantel pueda volver a usar ese correo con un alumno nuevo en el siguiente ciclo. Antes el correo seguía ocupado: al dar de alta a otro alumno con él la plataforma marcaba error o le asignaba la cuenta del graduado, con su nombre y su contraseña. Si ese correo ya se había liberado en un ciclo anterior se numera (\"_graduado2\", \"_graduado3\"…). No se modifica el correo de quien sigue inscrito como alumno en algún plantel o tiene otro rol en la plataforma, porque esa cuenta dejaría de funcionar.",
      },
      {
        tipo: "nuevo",
        modulo: "Escolar",
        descripcion:
          "El asistente \"Promover alumnos de grado\" descarga el archivo \"Alumnos graduados\" con los alumnos que se graduaron en esa promoción: apellidos, nombre, nivel, grado, el correo anterior y el actual, si el correo se liberó y, cuando no, el motivo. Se descarga al terminar el paso de promover y no junto con los reportes de actividades, porque hasta ese momento se sabe con qué correo queda cada graduado.",
      },
      {
        tipo: "mejora",
        modulo: "Escolar",
        descripcion:
          "\"Alumnos\", \"Personal del plantel\" y \"Padres, madres o tutores\" tienen \"Ordenar por: Nombre | Apellidos\" arriba de la tabla. Ordena la lista completa ya filtrada, no sólo la página que se ve, y arranca por apellidos. El nombre se separó en tres columnas —Nombre(s), Primer apellido y Segundo apellido— que se acomodan según el orden: por apellidos, los apellidos van primero, como en un pase de lista. \"Exportar\" sale con el mismo orden y las mismas tres columnas, con los encabezados de las plantillas de carga masiva. Antes el personal aparecía agrupado por rol y los padres o tutores en el orden en que llegaban del servidor.",
      },
      {
        tipo: "mejora",
        modulo: "Escolar",
        descripcion:
          "En la lista de alumnos, la columna \"Grupo\" muestra cada grupo como etiqueta, con el mismo estilo que las materias en \"Grupos y relaciones\", en lugar de una lista con viñetas. Si un alumno está en más de cinco grupos aparece \"Mostrar más\".",
      },
      {
        tipo: "correccion",
        modulo: "Escolar",
        descripcion:
          "La lista de alumnos vuelve a mostrar la foto de perfil. La consulta que alimenta la tabla había dejado de incluirla y todos los alumnos aparecían con el ícono genérico, aunque tuvieran foto.",
      },
      {
        tipo: "mejora",
        modulo: "Reportes",
        descripcion:
          "El reporte de evaluaciones del profesor tiene \"Ordenar por: Nombre | Apellidos\", como el de actividades. En los dos reportes el nombre va en tres columnas —Nombre(s), Primer apellido y Segundo apellido— que se acomodan según el orden, y la lista arranca por apellidos. Los archivos de Excel, incluido \"Exportar completo\" de actividades, salen con el mismo orden y las mismas columnas.",
      },
      {
        tipo: "mejora",
        modulo: "Reportes",
        descripcion:
          "En la pantalla de libros del reporte del profesor, cada evaluación se muestra con su nombre, con el estilo de las materias en \"Grupos y relaciones\". Antes eran círculos con un número y había que pasar el mouse por encima para saber cuál era cuál. Se ven hasta cinco evaluaciones por libro; si hay más, aparece \"Mostrar más\".",
      },
      {
        tipo: "mejora",
        modulo: "Reportes",
        descripcion:
          "La pantalla de libros del reporte del profesor carga más rápido. Pedía la información completa de cada lección de cada libro de la asignatura aunque sólo usa unos cuantos datos; ahora pide sólo esos. En una asignatura de 198 materias, lo que se descargaba pasó de 24 MB a 2.5 MB.",
      },
      {
        tipo: "mejora",
        modulo: "Herramientas",
        descripcion:
          "En \"Gestión de contraseñas\" la contraseña de cada alumno se ve directamente en la tabla, sin abrir un aviso con el ícono del ojo. La pantalla quedó con el estilo del resto de la plataforma: grupo y materia son listas desplegables junto al buscador, hay un contador de alumnos, una columna con el grupo, \"Ordenar por\" y el botón \"Atrás\" de las demás herramientas. El buscador ya no distingue acentos, también busca por correo y dejó de fallar con alumnos sin segundo apellido. A los padres o tutores sólo se les muestra el buscador.",
      },
      {
        tipo: "mejora",
        modulo: "Herramientas",
        descripcion:
          "En \"Reporte de sugerencias\", la ventana \"Conversación\" es más grande: ocupa un tercio del ancho de la pantalla y el 85% del alto desde que se abre, con la caja de respuesta siempre abajo. El texto que se escribe sale en gris, como en el resto de la plataforma, y no en azul, y la caja crece hasta tres renglones; a partir de ahí hace scroll.",
      },
      {
        tipo: "correccion",
        modulo: "Herramientas",
        descripcion:
          "En \"Detalles del reporte\" de \"Reporte de sugerencias\", el contador de la conversación ya cuenta sólo los mensajes que hay. Sumaba uno de más —el texto original del reporte, que ya no forma parte de la conversación—, así que con una sola respuesta decía \"2 mensajes\". Ahora dice \"1 mensaje\".",
      },
    ],
  },
  {
    version: "2.0.24-beta",
    fecha: "2026-09-02",
    cambios: [
      {
        tipo: "mejora",
        modulo: "Herramientas",
        descripcion:
          "Herramientas Nimbus GED está agrupada por temas. Antes era una sola cuadrícula con todo revuelto —lo del aula junto a lo de administración, los catálogos y los avisos—, y con más de treinta herramientas encontrar una era recorrer la página entera. Ahora cada tema es un bloque con su nombre, su icono y el número de herramientas que trae: Aula, Administración, Catálogos, Libros, UDA (Unidades de aprendizaje), NIA y Avisos. Los bloques que no tienen ninguna herramienta para tu rol no se muestran, así que cada quien ve sólo lo suyo. Las tarjetas ahora son horizontales —icono, nombre y el botón de información con la explicación de siempre—, y los iconos se rehicieron todos con el mismo trazo y el color de la plataforma, en vez de la mezcla de dibujos y fotos que había.",
      },
      {
        tipo: "mejora",
        modulo: "Herramientas",
        descripcion:
          "La sección \"Contenidos\" de Herramientas Nimbus GED se partió en dos, porque mezclaba herramientas que trabajan sobre el libro completo con otras que trabajan sobre las lecciones. En \"Libros\" quedaron \"Descarga de PDF y DOCX\" y \"Gestor de portadas\"; en \"UDA (Unidades de aprendizaje)\", \"Crear zip de todos los umaBloques\", \"Carga masiva de campos ad.\", \"Registro de cambios\" y \"Editor de fondos de texto\". Ninguna herramienta cambió de nombre ni de permisos: son las mismas, sólo agrupadas donde se buscan.",
      },
      {
        tipo: "mejora",
        modulo: "Herramientas",
        descripcion:
          "La herramienta \"Creador de herramientas\" ahora se llama \"Creador de herramientas externas\", que es lo que hace: da de alta accesos a herramientas de fuera de la plataforma, con su nombre y su enlace. El nombre anterior se confundía con crear herramientas de Nimbus GED.",
      },
      {
        tipo: "mejora",
        modulo: "Herramientas",
        descripcion:
          "Las categorías de materias se administran desde el propio \"Gestor de materias\", con el botón \"Categorías\" que está junto a \"Crear materia\", igual que en \"Editor de fondos de texto\". Por eso la tarjeta suelta \"Gestor de categorías de materias\" ya no aparece en Catálogos: no se quitó nada, se movió adentro de la herramienta con la que se usa. Quien podía entrar antes puede entrar igual.",
      },
      {
        tipo: "correccion",
        modulo: "Visor",
        descripcion:
          "NIA vuelve a respetar el modelo de IA que se elige. Los modelos que ofrecía el chatbot ya no existían en el servidor, así que la elección se descartaba en silencio y todo se respondía con el modelo por omisión; en \"Acciones predeterminadas NIA\", además, guardar una acción con uno de esos modelos devolvía un error. El selector ahora tiene dos opciones vigentes, \"Respuesta rápida\" y \"Respuesta avanzada\" —desapareció la intermedia, que ya no tiene modelo detrás— y el chatbot arranca en \"Respuesta rápida\". Las acciones que tenían fijado un modelo retirado se muestran como \"Automático\", que es como se venían ejecutando.",
      },
    ],
  },
  {
    version: "2.0.23-beta",
    fecha: "2026-09-01",
    cambios: [
      {
        tipo: "nuevo",
        modulo: "Herramientas",
        descripcion:
          "Nueva herramienta \"Gestor de NIA\": el director editorial decide en qué libros de su editorial se muestra NIA, el asistente de contenido que aparece dentro del visor. Cada libro trae su interruptor, y arriba están los botones \"Activar los de la lista\" y \"Desactivar los de la lista\", que aplican a los libros que se estén viendo en ese momento. La lista se puede acotar por nivel académico, grado, materia y estado (Con NIA / Sin NIA), y buscar por título; un contador indica cuántos libros tienen NIA activo del total. Los libros de autores independientes no aparecen. Importante: mientras un libro no se active aquí, NIA no se muestra en él, así que después de esta actualización hay que prenderlos uno por uno o en lote.",
      },
      {
        tipo: "mejora",
        modulo: "Visor",
        descripcion:
          "NIA ya no se muestra en los libros donde no tiene nada que ofrecer. El botón sólo aparece si el libro está activado en el \"Gestor de NIA\" y si el rol de quien entra tiene al menos una acción configurada en \"Acciones predeterminadas NIA\". Antes se pintaba siempre y al abrirlo podía quedar sin acciones, o mostrarse en libros donde la editorial no lo quería.",
      },
      {
        tipo: "correccion",
        modulo: "Visor",
        descripcion:
          "NIA ya responde sobre el libro que está abierto cuando se pasa de un libro a otro sin salir del visor. Antes se quedaba con el contenido del libro anterior —y con la lección que estuviera seleccionada en aquél—, así que un resumen podía hablar de lecciones que no existen en el libro que se está viendo. Ahora, al cambiar de libro, se vuelve a cargar su contenido y se suelta la lección que venía seleccionada.",
      },
      {
        tipo: "correccion",
        modulo: "Visor",
        descripcion:
          "Los resúmenes de libro ya cubren todas las lecciones. En libros largos la respuesta salía agrupada o cortada a media frase: un libro de 153 lecciones se resolvía en 21 apartados, y libros con lecciones que se llaman igual en cada unidad las fusionaban en una sola. Ahora el contenido se manda por bloques, se revisa que la respuesta traiga una entrada por lección y, si falta, se vuelve a pedir repartido en más partes. La respuesta se organiza por agrupador —unidad o proyecto— y dentro de cada uno las lecciones van numeradas y en el orden del libro. Si aun así queda incompleta, ya no se guarda para los demás usuarios.",
      },
      {
        tipo: "correccion",
        modulo: "Visor",
        descripcion:
          "Las respuestas en las que NIA se negaba a hacer la tarea (\"Lo siento, no puedo…\", \"Indícame qué lección quieres\") ya no se guardan ni se vuelven a servir. Antes una falla de una sola vez quedaba almacenada y todos los usuarios de ese rol recibían siempre esa misma negativa en ese libro. Además, al ejecutar una acción rápida ya no se le pide al usuario que seleccione un libro o una lección —el contenido ya viene resuelto— y si el contenido es escaso se resuelve con lo que hay, anotándolo al final, en vez de rechazar la tarea completa. Al alumno tampoco se le niega el resumen de una lección por estar armada con actividades: NIA sigue sin darle las respuestas de las evaluaciones, pero sí puede explicarle y resumirle el contenido.",
      },
      {
        tipo: "mejora",
        modulo: "Visor",
        descripcion:
          "El resumen de un libro tarda mucho menos en empezar. El contenido de las lecciones se pedía de una en una, así que en un libro grande se esperaban varios minutos antes de que la IA recibiera siquiera la primera palabra; ahora se piden de cinco en cinco, conservando el orden del libro.",
      },
      {
        tipo: "nuevo",
        modulo: "Herramientas",
        descripcion:
          "En \"Acciones predeterminadas NIA\" cada acción puede fijar el modelo de IA con el que se ejecuta. En \"Automático\" usa el modelo con el que arranca el chatbot, que es el que ve siempre el alumno y el que el profesor tiene al abrirlo; al elegir uno, ese manda para todos los usuarios de esa acción. Las acciones con modelo fijo lo muestran como etiqueta en la lista. Cambiarlo borra las respuestas ya guardadas de esa acción para que se generen de nuevo.",
      },
      {
        tipo: "correccion",
        modulo: "Reportes",
        descripcion:
          "Los alumnos aparecen en orden alfabético en el reporte de actividades por libro y en la ficha del profesor que ve la dirección. Antes llegaban en el orden en que el servidor terminaba de calcularlos, que cambiaba de una carga a otra: buscar a un alumno obligaba a recorrer la lista completa y dos consultas seguidas del mismo grupo se veían distintas. El orden respeta los acentos y no distingue mayúsculas, así que \"Ávila\" queda entre \"Ana\" y \"Carlos\". Aplica igual a la tabla en pantalla y a los dos archivos de Excel, \"exportar\" y \"exportar todo\".",
      },
      {
        tipo: "mejora",
        modulo: "Herramientas",
        descripcion:
          "En el DOCX, las actividades de ordenar pasos llevan el paréntesis donde el alumno escribe el número a la izquierda del paso, como en las de relacionar. Antes iba una línea de guiones bajos, que se lee como un renglón para escribir texto y no como una casilla para un número. Además, en anagrama y en ordenar palabras ya salen la imagen, el audio y el video del reactivo cuando la actividad los tiene: se perdían al exportar aunque en la plataforma sí se vieran.",
      },
      {
        tipo: "correccion",
        modulo: "Herramientas",
        descripcion:
          "En el DOCX, el banco de palabras de las actividades de palabra faltante se imprime con su recuadro. El borde gris claro que traía se perdía al imprimir y las palabras quedaban sueltas en la hoja, sin nada que las distinguiera del resto del texto.",
      },
    ],
  },
  {
    version: "2.0.22-beta",
    fecha: "2026-08-27",
    cambios: [
      {
        tipo: "nuevo",
        modulo: "Constructor",
        descripcion:
          "En la estructura de un libro los contenidos y los agrupadores se reordenan arrastrándolos: se toma la fila desde el icono de puntos que está al final y se suelta en su nueva posición. Antes había que subirlos o bajarlos de uno en uno con las flechas, así que en un nivel con treinta contenidos llevar el último hasta arriba obligaba a repetir el mismo clic decenas de veces. El arrastre sólo cambia el orden dentro del mismo nivel; para llevar algo a otro agrupador se sigue usando el botón de cambiar de ubicación. También funciona con el teclado: con la fila enfocada, la barra espaciadora la levanta, las flechas la mueven y la barra espaciadora la suelta.",
      },
      {
        tipo: "correccion",
        modulo: "Constructor",
        descripcion:
          "Ya se puede mover un agrupador con contenido dentro a otro agrupador siempre que la estructura resultante quepa en el libro. Antes se rechazaba con el aviso de que se excedía el nivel permitido aunque no fuera cierto: la revisión cortaba un nivel antes de lo que el libro admite, y por eso un agrupador con sus lecciones no se dejaba mover a un agrupador de segundo nivel, cuando el resultado cabía sin problema. Ahora se revisan por separado los agrupadores y las lecciones que trae dentro, porque el último nivel del libro sólo puede mostrar lecciones: si el movimiento dejaría un agrupador ahí, se avisa con un mensaje que dice cuál es el problema.",
      },
      {
        tipo: "correccion",
        descripcion:
          "Las materias que empiezan con acento ya aparecen en su lugar dentro de la lista. \"Ética naturaleza y sociedades\" salía siempre hasta el final, después de la última materia, porque el servidor las ordenaba comparando los caracteres uno por uno, sin considerar las reglas del español. El orden se corrigió en el servidor, en las consultas que entregan el catálogo de materias, así que aplica a todas las pantallas que las muestran: el selector de contenidos del creador de libros, el de gestión de equipos de trabajo, los filtros de materias y las listas al crear y editar una lección.",
      },
      {
        tipo: "mejora",
        modulo: "Constructor",
        descripcion:
          "En la estructura de un libro los botones de la columna de acciones se distinguen entre sí. El que abre la ventana para llevar un contenido a otro agrupador ahora dice \"Cambiar de ubicación\" y usa un icono de carpetas con niveles; el de arrastrar es un icono de puntos en gris, al final de la fila. Antes los dos hablaban de \"mover\" y se confundían, aunque uno cambia el orden dentro del nivel y el otro cambia de agrupador.",
      },
      {
        tipo: "mejora",
        modulo: "Constructor",
        descripcion:
          "Varios ajustes de acomodo en el creador de libros. Los botones de Cancelar y Guardar, el de Mostrar contenidos y la paginación quedan fijos al pie, y \"Seleccionar todos\" fijo arriba, así no se pierden de vista al recorrer listas largas. El selector de contenidos y la estructura del libro quedaron del mismo tamaño y con el mismo espacio arriba y abajo. Los botones de Vista previa, Estructura del libro, Materias, Agregar contenido, Atrás, Cancelar y Guardar se ajustaron al tamaño que tienen en el resto de la plataforma. La ventana para cambiar de ubicación muestra los agrupadores en filas más bajas y con los colores de la plataforma.",
      },
    ],
  },
  {
    version: "2.0.21-beta",
    fecha: "2026-08-26",
    cambios: [
      {
        tipo: "correccion",
        modulo: "Constructor",
        descripcion:
          "El contador de contenidos de las carpetas ya coincide con lo que hay dentro. Antes se llevaba una cuenta aparte que se iba desajustando con el uso: al borrar un contenido no se descontaba, y al mandarlo a la papelera o recuperarlo sólo se ajustaba si la carpeta era de quien hacía la acción. Por eso una carpeta podía decir 20 y tener 11, u otra decir 37 y tener 45. Ahora el número se cuenta en el momento de mostrarlo, y cuenta exactamente los contenidos que se ven al entrar. Aplica igual a las carpetas del creador de contenidos y a las de libros.",
      },
      {
        tipo: "mejora",
        modulo: "Constructor",
        descripcion:
          "La pantalla \"Editar campos\" de un libro abre y guarda mucho más rápido. Antes descargaba el libro completo —todas sus lecciones con sus bloques y actividades— para llenar un formulario que sólo usa los datos del libro, y al guardar lo volvía a descargar entero: en un libro grande eran varios segundos y varios megas en cada paso. Ahora sólo pide los campos del libro. Además el indicador de carga se mantiene hasta que toda la información está lista: antes desaparecía antes de tiempo y la ventana se veía vacía un rato hasta que los datos aparecían solos.",
      },
      {
        tipo: "mejora",
        modulo: "Constructor",
        descripcion:
          "Mientras se guarda un libro desde \"Editar campos\" ya no se puede seguir modificando el formulario: los campos, las listas de autores, temas y materias, la portada y el botón de cancelar quedan bloqueados hasta que termina. Se notaba sobre todo en libros grandes, donde el guardado tardaba y se alcanzaba a escribir encima. Si el guardado falla, el formulario se desbloquea y avisa, en vez de dar por hecho que se guardó.",
      },
      {
        tipo: "mejora",
        modulo: "Herramientas",
        descripcion:
          "En Descarga de PDF y DOCX ahora hay sólo dos botones, PDF y DOCX, tanto en cada libro como al seleccionar varios. Al pulsar uno se abre una ventana con las opciones de ese formato y se elige cuál descargar, en lugar de tener todas las combinaciones como botones sueltos. Los dos lugares ofrecen las mismas opciones; antes no coincidían.",
      },
      {
        tipo: "mejora",
        modulo: "Herramientas",
        descripcion:
          "En el DOCX, las preguntas de falso y verdadero salen con las columnas en el orden Pregunta, Verdadero, Falso. En la hoja del profesor la respuesta correcta se marca con una X, y el resto de las casillas quedan vacías para que el alumno escriba ahí la suya.",
      },
    ],
  },
  {
    version: "2.0.20-beta",
    fecha: "2026-08-24",
    cambios: [
      {
        tipo: "nuevo",
        modulo: "Herramientas",
        descripcion:
          "Nueva herramienta \"Historial de lecciones\": lista las lecciones de la editorial que han tenido movimientos, de la edición más reciente a la más vieja, con su identificador, su nombre, si es lección o evaluación, cuándo se editó por última vez y cuántos movimientos lleva. Al seleccionar una se abre su historial completo: qué se hizo —agregar, editar, eliminar o apagar un bloque, o editar la información de la lección—, quién lo hizo con su rol, y la fecha y hora. Trae buscador por identificador o por nombre. La herramienta está acotada a la editorial del usuario.",
      },
      {
        tipo: "mejora",
        modulo: "Constructor",
        descripcion:
          "El historial de una lección ya no registra únicamente los movimientos de sus bloques constructores: también registra cuando se edita la información de la lección, y anota qué campos cambiaron (Título, Materia, Grados académicos, Objetivos de aprendizaje, etc.) en vez de sólo dejar constancia de que hubo una edición. Los campos que solo existen en reactivos y exámenes, como la longitud de códigos o el tiempo límite, no se toman en cuenta cuando el contenido es una lección.",
      },
      {
        tipo: "mejora",
        modulo: "Constructor",
        descripcion:
          "La pantalla de editar la información de una lección abre mucho más rápido: antes descargaba la lección completa para llenar un formulario que no usa mucha información, y ahora sólo pide los campos de la lección. Además el indicador de carga se mantiene hasta que toda la información está lista: antes desaparecía antes de tiempo y el formulario se veía llenándose solo, en los exámenes aparecía vacío de entrada, y en una lección sin nivel académico se quedaba girando para siempre.",
      },
    ],
  },
  {
    version: "2.0.19-beta",
    fecha: "2026-08-21",
    cambios: [
      {
        tipo: "nuevo",
        modulo: "Constructor",
        descripcion:
          "Las tarjetas de lección tienen la opción \"Ver libros\" en su menú de tres puntos: abre la lista de los libros en los que está armada esa lección y, al hacer clic en uno, entra directo a su visor. Sirve para los reportes que llegan con el nombre de la lección pero sin el libro, en vez de ir abriendo libro por libro a buscarla. Los libros que están en la papelera aparecen marcados. Solo para editor editorial.",
      },
      {
        tipo: "nuevo",
        modulo: "Herramientas",
        descripcion:
          "En Gestión de PDFs de libros hay un botón nuevo, \"DOCX Alumno + Profesor (ZIP)\", que descarga los dos documentos de un libro en un solo paso, tanto por libro como en lote. Las actividades que se revuelven al exportar (relacionar, arrastrar, sopa de letras, anagrama) salen en el mismo orden en ambos, así que la hoja de respuestas del profesor corresponde actividad por actividad con la del alumno. En las acciones masivas del home de contenidos es una opción más del modal de Descargar Word, \"Alumno + Profesor\", que genera los dos documentos por lección.",
      },
      {
        tipo: "mejora",
        modulo: "Herramientas",
        descripcion:
          "Al descargar el DOCX de alumno y el de profesor juntos, el ZIP trae una sola carpeta de imágenes con todas las que usan los dos documentos, en vez de una carpeta por documento con las mismas imágenes repetidas. Cada imagen se guarda una sola vez, aunque la usen los dos.",
      },
      {
        tipo: "mejora",
        modulo: "Herramientas",
        descripcion:
          "En el DOCX, las líneas sobre las que el alumno escribe a mano (los huecos de palabra faltante, las celdas de arrastrar, los renglones de respuesta abierta y la casilla del número de orden) tienen el trazo a la mitad de grueso, sin cambiar de largo: se ven como una línea para escribir y no como una barra. Además, en las celdas de arrastrar el renglón es el doble de alto, para que quepa la respuesta escrita a mano.",
      },
      {
        tipo: "correccion",
        modulo: "Herramientas",
        descripcion:
          "En el DOCX, cada lista numerada vuelve a empezar en 1. Antes continuaba la cuenta de la lista anterior de la lección: una actividad de Ordenar de 6 pasos podía salir numerada del 7 al 12 si arriba había otra lista de 6 elementos. Las listas que declaran un inicio distinto (por ejemplo, empezar en 3) lo siguen respetando.",
      },
    ],
  },
  {
    version: "2.0.18-beta",
    fecha: "2026-08-20",
    cambios: [
      {
        tipo: "mejora",
        modulo: "General",
        descripcion:
          "Los correos electrónicos se guardan siempre en minúsculas y sin espacios, tanto al iniciar sesión como al dar de alta usuarios. Si escribes o pegas \"Juan PEREZ@Correo.com\", se registra como \"juanperez@correo.com\". Antes sólo se quitaban los espacios de los extremos, así que un espacio a media dirección o un correo capturado con mayúsculas se guardaba tal cual y después no dejaba entrar a la plataforma.",
      },
      {
        tipo: "mejora",
        modulo: "General",
        descripcion:
          "La limpieza del correo aplica en todos los formularios donde se captura: alta y edición de usuarios, alumnos, tutores y familias, usuarios de escuelas, distribuidores y editoriales, Mi perfil, y la pantalla de recuperar contraseña.",
      },
      {
        tipo: "mejora",
        modulo: "General",
        descripcion:
          "En las cargas masivas de usuarios, alumnos y tutores, los correos del archivo de Excel se limpian al cargarlo: se quitan mayúsculas, espacios y los caracteres invisibles que suelen venir al copiar y pegar desde Word o desde otra hoja. En el archivo de tutores también se limpia la columna de correos de los hijos, que es la que los liga con sus alumnos.",
      },
    ],
  },
  {
    version: "2.0.17-beta",
    fecha: "2026-08-18",
    cambios: [
      {
        tipo: "mejora",
        modulo: "Constructor",
        descripcion:
          "En el editor de texto, las herramientas propias de Nimbus (Mimi Math, Nimbus Math, Nimbus Chem, División, Comentario, color e imagen de fondo y Ajustar imagen) pasaron al final de la barra, después de los botones del editor. Antes quedaban intercaladas a media barra, entre la alineación y el resaltado.",
      },
      {
        tipo: "correccion",
        modulo: "Constructor",
        descripcion:
          "El menú de tres puntos de las tarjetas vuelve a abrir después de entrar a una lección y regresar al listado. Antes el botón dejaba de responder y había que recargar la página para volver a usar Añadir a carpeta, Duplicar o Editar. Aplica igual a las tarjetas de reactivos, tareas y libros.",
      },
      {
        tipo: "correccion",
        modulo: "General",
        descripcion:
          "Cuando se publica una versión nueva de la plataforma y tienes una pestaña abierta de antes, la página se actualiza sola al entrar a una sección. Antes aparecía un error de carga y la sección se quedaba en blanco hasta recargar a mano.",
      },
    ],
  },
  {
    version: "2.0.16-beta",
    fecha: "2026-08-17",
    cambios: [
      {
        tipo: "nuevo",
        modulo: "Constructor",
        descripcion:
          "Los bloques que se editan en su propia pantalla (Preguntas, Preguntas abiertas, Relacionar, Memorama, Crucigrama, Checklist y Autoevaluación) tienen ahora su propio botón de Importar en la barra del bloque: carga un archivo JSON y cambia únicamente el contenido de ese bloque, conservando su posición en la lección y sus interruptores de Profesor y Activo. Si el archivo trae varios bloques, se usa el primero que sea del mismo tipo. Solo para editor editorial.",
      },
      {
        tipo: "nuevo",
        modulo: "Constructor",
        descripcion:
          "En los bloques de Preguntas y Preguntas abiertas, el botón de Importar ofrece dos opciones: \"Reemplazar\", que sustituye todo el contenido, y \"Agregar debajo\", que conserva las preguntas que ya tiene el bloque y le suma las del archivo al final. Así se puede completar un bloque de 5 preguntas hasta 15 sin volver a capturar las primeras. Las instrucciones y la configuración del bloque no se tocan al agregar.",
      },
      {
        tipo: "nuevo",
        modulo: "Constructor",
        descripcion:
          "La previsualización de actividades tiene un interruptor de Modo profesor para alternar entre la actividad resuelta y la actividad como la contesta el alumno, sin salir del constructor. Está en Arrastrar, Crucigrama, Dibujar, Hotspot, Memorama, Ordenar, Ordenar palabras, Preguntas, Relacionar, Rompecabezas y Sopa de letras.",
      },
      {
        tipo: "mejora",
        modulo: "Acciones masivas",
        descripcion:
          "Al descargar lecciones en Word, cada archivo lleva el identificador de la lección antes de su nombre (por ejemplo, \"L01_Los números enteros_alumno.docx\"), de modo que los archivos quedan ordenados igual que en el listado de contenidos.",
      },
      {
        tipo: "mejora",
        modulo: "Constructor",
        descripcion:
          "Las listas del editor de texto se ven igual en el editor, en el constructor y en el visor: la numeración anidada vuelve a seguir su jerarquía (1, a, i, A, I) en vez de quedarse siempre en números, el texto conserva el color y el tamaño elegidos en lugar de forzarse a gris, y se quitó la sangría de más. En las listas centradas o alineadas a la derecha, la viñeta o el número viajan junto al texto.",
      },
      {
        tipo: "correccion",
        modulo: "Constructor",
        descripcion:
          "Importar un Checklist desde un archivo JSON ya crea sus elementos: antes el bloque se creaba vacío. Al importar sobre un checklist que ya existe, sus elementos se actualizan, se agregan los nuevos y se eliminan los que ya no vienen en el archivo.",
      },
      {
        tipo: "correccion",
        modulo: "Herramientas",
        descripcion:
          "En el DOCX, las imágenes que van dentro de una tabla ya se ajustan al ancho de su columna. Antes se medían contra el ancho de la hoja completa, así que en tablas de dos o tres columnas salían enormes y se desbordaban de la página; había que encogerlas a mano en Word. Se respetan los anchos de columna que se hayan definido al redimensionar la tabla, y las imágenes que están fuera de tablas no cambian.",
      },
      {
        tipo: "mejora",
        modulo: "Herramientas",
        descripcion:
          "En el DOCX las listas ya se exportan como listas de verdad, no como texto con el número escrito a mano. Word las renumera solo al agregar o quitar elementos, se pueden seguir editando como lista, cambiarles la sangría desde la regla, y el número o la viñeta dejan de ser texto que se puede seleccionar o borrar por error.",
      },
      {
        tipo: "correccion",
        modulo: "Herramientas",
        descripcion:
          "En el DOCX, las listas dentro de otra lista ya se numeran por nivel (1., luego a., luego i.) en lugar de reiniciar en 1. en cada sublista, y cada nivel se sangra el doble que antes para que la jerarquía se distinga.",
      },
      {
        tipo: "correccion",
        modulo: "Herramientas",
        descripcion:
          "En el DOCX ya no se pierden ni se aplanan las sublistas de textos pegados desde Word o Google Docs. Según cómo quedara el pegado, sus elementos aparecían corridos dentro del renglón de arriba, sin número ni sangría, o desaparecían del documento.",
      },
    ],
  },
  {
    version: "2.0.15-beta",
    fecha: "2026-08-13",
    cambios: [
      {
        tipo: "mejora",
        modulo: "Visor",
        descripcion:
          "El nombre de la lección con su identificador pasó del pie del visor a la barra superior, centrado entre el botón de Sugerencias y el switch de profesor. Aparece al hacer scroll, en cuanto el título de la lección deja de verse, y se oculta al volver arriba.",
      },
      {
        tipo: "mejora",
        modulo: "Visor",
        descripcion:
          "Los botones de lección anterior y siguiente quedaron centrados en la franja del pie del visor, que antes los dejaba pegados al borde inferior.",
      },
      {
        tipo: "correccion",
        modulo: "Visor",
        descripcion:
          "La zona para hacer clic en los botones de lección anterior y siguiente ya se limita al botón: antes se extendía a lo ancho de la pantalla y se cambiaba de lección al hacer clic en un espacio vacío.",
      },
      {
        tipo: "mejora",
        modulo: "Constructor",
        descripcion:
          "Los bloques de texto con color de fondo ahora dejan un margen interno para que el texto no quede pegado a las orillas. Se ve tanto en el constructor como en el visor, y aplica a las lecciones que ya existen sin tener que volver a editarlas.",
      },
      {
        tipo: "correccion",
        modulo: "Constructor",
        descripcion:
          "En el bloque de preguntas, las operaciones verticales de Mimi Math ya se ven alineadas. Los renglones se descuadraban tanto en el recuadro del editor como en el visor y en el visor de exámenes, tanto en la pregunta como en las respuestas, mientras que en el bloque de texto se veían bien. Las operaciones ya capturadas se corrigen solas, sin volver a editarlas.",
      },
    ],
  },
  {
    version: "2.0.14-beta",
    fecha: "2026-08-12",
    cambios: [
      {
        tipo: "correccion",
        modulo: "Constructor",
        descripcion:
          "Al guardar un bloque de una lección ya no aparece de forma intermitente el error que impedía guardar. El contenido se guardaba, pero la pantalla se quedaba cargando sin avisar.",
      },
      {
        tipo: "correccion",
        modulo: "Constructor",
        descripcion:
          "Duplicar una lección ya no falla en las lecciones con mucho material. Además, la lección duplicada conserva sus recursos descargables, que antes no quedaban disponibles.",
      },
      {
        tipo: "correccion",
        modulo: "Acciones masivas",
        descripcion:
          "La duplicación de lecciones desde plantilla dejó de interrumpirse a media lista por el mismo problema.",
      },
      {
        tipo: "correccion",
        modulo: "Constructor",
        descripcion:
          "En la actividad de Hotspot, las instrucciones ya no se borran al guardar en \"Hotspot múltiple\" y \"Encontrar las diferencias\": antes no se cargaban al abrir la actividad y se perdían al guardar.",
      },
      {
        tipo: "mejora",
        modulo: "Constructor",
        descripcion:
          "En la actividad de Relacionar, si el guardado falla ahora se muestra el motivo y la pantalla deja de quedarse cargando.",
      },
    ],
  },
  {
    version: "2.0.13-beta",
    fecha: "2026-08-11",
    cambios: [
      {
        tipo: "nuevo",
        modulo: "Herramientas",
        descripcion:
          "Los fondos de texto ya se pueden organizar por categorías: pantalla para crear, renombrar, activar o eliminar categorías, y filtro por categoría en el gestor de fondos.",
      },
      {
        tipo: "nuevo",
        modulo: "Visor",
        descripcion:
          "En el pie del visor, el nombre de la lección se muestra junto a su identificador. Solo para editor editorial, editor escolar y autor independiente.",
      },
      {
        tipo: "correccion",
        modulo: "Herramientas",
        descripcion:
          "El DOCX ya conserva el formato del texto de todos los bloques: negritas y cursivas que se perdían dentro de las listas o cuando venían de una regla de estilo (CSS) del bloque, además de subrayado y tachado.",
      },
      {
        tipo: "mejora",
        modulo: "Herramientas",
        descripcion:
          "En el DOCX las listas numeradas respetan su numeración de inicio y su tipo (1, a, A, i, I), las viñetas cambian según el nivel y los enlaces se insertan como hipervínculos reales, no solo como texto azul.",
      },
      {
        tipo: "mejora",
        modulo: "Herramientas",
        descripcion:
          "En el DOCX, las opciones del bloque de Preguntas se listan con incisos (a, b, c) y la respuesta correcta se marca en negrita, en vez de casillas y la leyenda \"(correcta)\".",
      },
      {
        tipo: "correccion",
        modulo: "Constructor",
        descripcion:
          "En el bloque de Preguntas abiertas, el tipo de presentación \"Lista\" ya se muestra como seleccionado en vez de quedarse en \"Seleccionar\".",
      },
    ],
  },
  {
    version: "2.0.12-beta",
    fecha: "2026-08-10",
    cambios: [
      {
        tipo: "nuevo",
        modulo: "Constructor",
        descripcion:
          "El bloque de HTML ya permite importar un archivo .html o .htm desde la computadora y exportar el contenido actual como archivo HTML.",
      },
      {
        tipo: "mejora",
        modulo: "Constructor",
        descripcion:
          "Control más preciso del tamaño de las imágenes en el editor visual del bloque de HTML: porcentajes predefinidos, porcentaje personalizado, ancho máximo en píxeles (deslizador o captura directa) y botón para volver al tamaño original.",
      },
    ],
  },
  {
    version: "2.0.11-beta",
    fecha: "2026-08-06",
    cambios: [
      {
        tipo: "nuevo",
        modulo: "Acciones masivas",
        descripcion: "Generación masiva de DOCX por lección para libros. Esto desde el home de las lecciones.",
      },
      {
        tipo: "nuevo",
        modulo: "Herramientas",
        descripcion:
          "La actividad de Memorama ya se incluye en el DOCX como hoja de tarjetas para recortar: tres columnas, todas las tarjetas del mismo tamaño y con el contorno punteado como línea de corte. En la versión de profesor cada tarjeta lleva el número de su pareja.",
      },
      {
        tipo: "nuevo",
        modulo: "General",
        descripcion:
          "Nueva bitácora de novedades para el ambiente de desarrollo: muestra la versión publicada y los cambios de cada actualización.",
      },
      {
        tipo: "correccion",
        modulo: "Herramientas",
        descripcion:
          "Ya no se imprimen las reglas de estilo (CSS) como texto dentro del DOCX cuando el bloque de texto trae HTML pegado.",
      },
      {
        tipo: "correccion",
        modulo: "Constructor",
        descripcion:
          "Se corrigió el pegado de contenido con fuentes y estilos externos en el editor del bloque de texto.",
      },
      {
        tipo: "correccion",
        modulo: "Constructor",
        descripcion:
          "Se corrigió la orientación de los títulos y la eliminación de espacios en etiquetas de negritas.",
      },
    ],
  },
];

/** Versión más reciente publicada en la bitácora. */
export const versionActual = CHANGELOG[0]?.version ?? "";

export default CHANGELOG;
